import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import axiosInstance from '@/utils/axiosInstance';

const COUNTRY_FLAG = {
  BE: '🇧🇪', NL: '🇳🇱', FR: '🇫🇷', DE: '🇩🇪', UK: '🇬🇧',
};

export default function AlertHistory() {
  const { id } = useParams();
  const { t } = useTranslation('alerts');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axiosInstance
      .get(`/api/alerts/${id}/history/`, { params: { days: 30 } })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.status === 404 ? 'notFound' : 'fetch'))
      .finally(() => setLoading(false));
  }, [id]);

  const chartData = (data?.history || []).map(h => ({
    date: h.date_iso,
    shortDate: h.date_iso ? h.date_iso.slice(5) : h.date,
    count: h.violations_count,
  }));

  const totalViolations = chartData.reduce((s, d) => s + d.count, 0);
  const daysWithViolations = chartData.filter(d => d.count > 0).length;
  const maxViolations = chartData.length > 0 ? Math.max(...chartData.map(d => d.count)) : 0;

  return (
    <Layout>
      <PageHeader title={
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/alerts')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">{t('history.title')}</h1>
        </div>
      } />

      <div className="space-y-6">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center space-y-2">
              <AlertTriangle className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="text-base font-medium">
                {error === 'notFound' ? t('history.notFound') : t('history.error')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary */}
            <Card className="border-amber-200">
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('history.alertLabel')}
                </p>
                <h2 className="text-xl font-semibold mb-4">{data.alert.name}</h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('history.thresholdLabel')}</p>
                    <p className="font-semibold">{data.alert.threshold_pct}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('history.countriesLabel')}</p>
                    <p className="font-semibold">
                      {data.alert.countries.length === 0
                        ? t('allCountries')
                        : data.alert.countries.map(c => COUNTRY_FLAG[c] || c).join(' ')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('history.daysWithViolations')}</p>
                    <p className="font-bold text-2xl text-amber-700">{daysWithViolations}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('history.totalViolations')}</p>
                    <p className="font-bold text-2xl text-red-700">{totalViolations}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Chart */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4">{t('history.chartTitle', { days: data.days })}</h3>
                {chartData.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('history.noData')}
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#fef3c7" />
                      <XAxis dataKey="shortDate" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#fffbeb',
                          border: '1px solid #fde68a',
                          borderRadius: '6px',
                        }}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label}
                        formatter={(value) => [value, t('history.tooltipViolations')]}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, idx) => (
                          <Cell
                            key={idx}
                            fill={entry.count === 0 ? '#e5e7eb' : '#f59e0b'}
                            cursor="pointer"
                            onClick={() => entry.count > 0 && navigate(`/alerts/${id}/report/${entry.date}`)}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Trigger days list */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-3">{t('history.triggerDaysTitle')}</h3>
                {daysWithViolations === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('history.noTriggers')}</p>
                ) : (
                  <ul className="divide-y">
                    {[...chartData].reverse().filter(d => d.count > 0).map(d => (
                      <li key={d.date}>
                        <Link
                          to={`/alerts/${id}/report/${d.date}`}
                          className="flex items-center justify-between py-3 hover:bg-muted/40 px-2 rounded transition-colors"
                        >
                          <span className="font-medium">{d.date}</span>
                          <span className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {t('history.violationsLine', { count: d.count })}
                            </span>
                            <span className="text-amber-700">→</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
